/* =====================================================================
   Ajustes → Suscripción: lo que se calcula sin pintar · Peaje U-04
   =====================================================================
   Tres fuentes, y cada dato sale de UNA (condición del integrador):
   · `GET /api/peaje/suscripcion`  el estado de la escalera: estado,
     color, días de mora, siguiente paso y su fecha, cancelación pedida.
   · `GET /api/escala/suscripcion` lo pactado: precio, ciclo, descuento,
     cargo próximo, líneas con enlace de pago y la lista de precios
     (`lista`, el mismo documento de `GET /api/publico/precios`).
   · `GET /api/planes`             qué planes acepta este servidor al
     pedirlos (`activo`). Solo se ofrece lo que se puede pedir: así el
     «Plan '…' no válido» de `pagos.plan_por_clave` no tiene por dónde
     salir desde esta tarjeta.
   Ningún precio sale de `plan.precio_anual` ni está escrito aquí.
   ===================================================================== */

// Dinero con separador de miles y dos decimales: «$1,234.00».
export function dinero(valor, moneda = 'MXN') {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  if (!Number.isFinite(n)) return null;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: moneda || 'MXN',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

// «2026-10-05» → «5 de octubre de 2026». La fecha ya es un día de la
// empresa (la calcula la nube): se pinta tal cual, sin moverla de huso.
const LARGA = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
});
export function fechaLarga(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  return LARGA.format(new Date(Date.UTC(a, m - 1, d)));
}

// El estado con su palabra y su color (verde bien, amarillo atención,
// rojo problema). El color lo manda el servidor; si no llega, se deduce
// igual que allá (`peaje/estado.py:COLOR`).
export const ESTADOS = {
  AL_CORRIENTE: { texto: 'Al corriente', color: 'verde' },
  GRATIS: { texto: 'Gratis', color: 'verde' },
  CANCELADO: { texto: 'Cancelado', color: 'verde' },
  PEDIDO: { texto: 'Plan pedido', color: 'amarillo' },
  VENCIDO: { texto: 'Pago vencido', color: 'amarillo' },
  CONSULTA: { texto: 'En consulta', color: 'rojo' },
};

export const PINTA_COLOR = {
  verde: 'bg-green-50 text-green-800 border-green-200',
  amarillo: 'bg-amber-50 text-amber-900 border-amber-200',
  rojo: 'bg-red-50 text-red-800 border-red-200',
};

export function estadoVisible(susc) {
  const e = ESTADOS[susc?.estado];
  if (!e) return null;
  return { ...e, color: susc.color || e.color };
}

// Lo que pasa en el siguiente peldaño de la escalera, en una frase.
export function textoDelPaso(paso, nombreInicial) {
  switch (paso) {
    // H-5 (auditoría U-04): sin domiciliación, «reintentar» es cambiar el
    // enlace vencido por uno nuevo; y el aviso del día 7 solo se anota en
    // TEGO Cloud (el envío llega con U-08). Se dice lo que pasa, nada más.
    case 'REINTENTO':
    case 'REINTENTO_Y_AVISO': return 'el enlace de pago vencido se cambia por uno nuevo';
    case 'CONSULTA': return `lo que el plan ${nombreInicial} no trae queda en consulta: se ve, pero no se registra nada nuevo`;
    case 'GRATIS': return `la empresa pasa al plan ${nombreInicial} con todos sus datos`;
    default: return null;
  }
}

// ¿Rige la escalera de mora? (vuelta 6). Solo un `true` explícito: si la
// nube la tiene apagada, o un servidor de antes no manda el campo, la
// tarjeta no pinta peldaños ni «al fin del periodo». Lo seguro es no prometer.
export function escaleraRige(esc) {
  return esc?.escalera === true;
}

// ¿Hay días pagados por delante? Solo AL_CORRIENTE los tiene; en VENCIDO
// y CONSULTA el periodo pagado ya terminó (H-4 de la auditoría U-04).
export function conPeriodoPagado(esc) {
  return esc?.estado === 'AL_CORRIENTE';
}

/** ¿La cancelación espera al fin del periodo? Solo con días pagados por
    delante Y la escalera encendida: apagada, TEGO Cloud aplica la
    cancelación en cuanto la recibe (nube 052), queden días o no. */
export function cancelaAlFinDelPeriodo(esc) {
  return conPeriodoPagado(esc) && escaleraRige(esc);
}

/** La consecuencia de cancelar, en una frase, ANTES de pulsar. Al corriente,
    con la fecha del último día pagado (`proximo_paso_en` del CARGO =
    `fin`); en mora no hay periodo que esperar y se dice así. */
export function consecuenciaDeCancelar(esc, nombrePlan, nombreInicial) {
  if (conPeriodoPagado(esc) && !escaleraRige(esc)) {
    return `Al confirmarlo, la empresa pasa al plan ${nombreInicial} en cuanto TEGO Cloud lo recibe, `
      + `aunque tu plan ${nombrePlan} todavía tenga días pagados, y conserva todos sus datos.`;
  }
  if (!conPeriodoPagado(esc)) {
    return `Tu plan ${nombrePlan} ya no tiene días pagados: al confirmarlo, la empresa pasa al plan `
      + `${nombreInicial} en cuanto TEGO Cloud lo recibe, y conserva todos sus datos.`;
  }
  const fin = esc.proximo_paso === 'CARGO' ? fechaLarga(esc.proximo_paso_en) : null;
  if (fin) {
    return `Tu plan ${nombrePlan} sigue funcionando hasta el ${fin}; después la empresa pasa al plan `
      + `${nombreInicial} y conserva todos sus datos.`;
  }
  return `Tu plan ${nombrePlan} deja de renovarse y la empresa pasa al plan ${nombreInicial} con todos sus datos.`;
}

/** Lo que se confirma DESPUÉS de pedir la cancelación. La fecha sale de
    `efectivo_desde` de la respuesta; nunca del texto fijo del servidor, que
    en mora prometía un periodo que no había (H-4). */
export function textoDeCancelacionPedida(respuesta, esc, nombreInicial) {
  const desde = fechaLarga(respuesta?.efectivo_desde);
  const deshacer = ' Antes de esa fecha, «Seguir con mi plan» lo deshace.';
  const alFin = cancelaAlFinDelPeriodo(esc) && respuesta?.inmediata !== true;
  if (desde) {
    return `Queda pedido: el plan ${nombreInicial} rige desde el ${desde}, con todos tus datos.`
      + (alFin ? deshacer : '');
  }
  if (!alFin) {
    return `Queda pedido: la empresa pasa al plan ${nombreInicial} en cuanto TEGO Cloud lo recibe, con todos sus datos.`;
  }
  const fin = esc.proximo_paso === 'CARGO' ? fechaLarga(esc.proximo_paso_en) : null;
  if (fin) {
    return `Queda pedido: tu plan sigue hasta el ${fin} y al día siguiente rige el plan ${nombreInicial}, con todos tus datos.`
      + deshacer;
  }
  return `Queda pedido. La fecha en que rige el plan ${nombreInicial} aparece en «Cambio pedido» en cuanto TEGO Cloud lo confirma.`;
}

export function planDeLaLista(lista, clave) {
  const planes = Array.isArray(lista?.planes) ? lista.planes : [];
  return planes.find((p) => p.clave === clave) || null;
}

export function nombreDePlan(lista, clave) {
  if (!clave) return null;
  const p = planDeLaLista(lista, clave);
  if (p?.nombre) return p.nombre;
  return clave.charAt(0).toUpperCase() + clave.slice(1);
}

// El precio publicado de un plan para un ciclo: la cifra con IVA de la
// lista, SOLO para planes de pago (lo usa «Planes con más»; Gratis nunca
// sale ahí). Un ciclo no ofrecido viene en null y así se queda; en un plan
// de pago una cifra de 0 o menos se trata igual: no se ofrece. Para Gratis,
// cuyo 0 es su precio real, no se usa esta función.
export function precioDeLista(plan, ciclo) {
  const p = plan?.precios?.[ciclo];
  if (!p) return null;
  const c = Number(p.con_iva);
  return Number.isFinite(c) && c > 0 ? c : null;
}

/** Los planes a los que se puede SUBIR: los de la lista con más orden
    que el actual, con precio mensual o anual publicado, y que este
    servidor acepta al pedirlos (`/api/planes`). */
export function planesParaMejorar(lista, planActual, clavesPedibles) {
  const planes = Array.isArray(lista?.planes) ? lista.planes : [];
  const actual = planes.find((p) => p.clave === planActual);
  const ordenActual = actual ? Number(actual.orden ?? 0) : -Infinity;
  const pedibles = clavesPedibles ? new Set(clavesPedibles) : null;
  return planes
    .filter((p) => p.clave !== planActual)
    .filter((p) => Number(p.orden ?? 0) > ordenActual)
    .filter((p) => precioDeLista(p, 'mensual') !== null || precioDeLista(p, 'anual') !== null)
    .filter((p) => !pedibles || pedibles.has(p.clave))
    .sort((a, b) => Number(a.orden ?? 0) - Number(b.orden ?? 0));
}

// El estado de la escalera, UNA lectura compartida entre el cartel del
// módulo y quien la pida después. Falla hacia «nada que decir»: sin
// respuesta, sin cartel de mora (el 403 del servidor sigue diciendo lo
// suyo al intentar guardar).
let promesa = null;
export function estadoDeLaEscalera(api) {
  if (!promesa) {
    promesa = api.get('/peaje/suscripcion').catch(() => null);
  }
  return promesa;
}
export function olvidarEstadoDeLaEscalera() {
  promesa = null;
}

/** ¿El cierre de este módulo es por falta de pago? La misma regla que
    `escala/modulos_cuenta.veredicto`: cerrado por el PLAN y la empresa en
    CONSULTA. Lo cerrado por una decisión de la cuenta no es mora. */
export function cerradoPorPago(fila, escalera) {
  if (!fila || !fila.en_consulta) return false;
  if (String(fila.origen || '').toUpperCase() !== 'PLAN') return false;
  return escalera?.estado === 'CONSULTA';
}
